// src/components/RechargeModal.js
import React, { useState, useEffect } from 'react';
import styles from '../styles/RechargeModal.module.css';
import { getProducts } from '../api/productApi';
import { fetchPayURL } from '../api/orderApi';
import { useSelector } from 'react-redux';
import AlertModal from './AlertModal';
import { useTranslation } from 'react-i18next'; // 添加这行导入

const RechargeModal = ({ isOpen, onClose, currentTab, userEmail }) => {
  // 使用 i18n 的翻译钩子
  const { t } = useTranslation();
  
  // const [currentMember, setCurrentMember] = useState(currentTab || 'gpt');
  const [currentMember, setCurrentMember] = useState('gpt');

  const [currentDuration, setCurrentDuration] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState('basic');
  const [currentPayment, setCurrentPayment] = useState('alipay');
  const [isProcessing, setIsProcessing] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false); // 新增：控制 AlertModal
  const [alertMessage, setAlertMessage] = useState(''); // 新增：AlertModal 消息
  const [successUrl, setSuccessUrl] = useState(null); // 新增：记录支付 URL

  const user = useSelector((state) => state.user);
  const user_key = user.user?.user_key || localStorage.getItem('user_key');
  const uid = user.user?.userToken || localStorage.getItem('userToken');

  // 价格说明文字配置 - 使用t函数进行国际化
  const priceNoteText = {
    gpt: t('rechargeModal.priceNote.gpt'),
    // deepseek: t('rechargeModal.priceNote.deepseek'),
    // sora: t('rechargeModal.priceNote.sora'),
    // gpt_api: t('rechargeModal.priceNote.gptApi'),
    claude: t('rechargeModal.priceNote.claude'),
    grok: t('rechargeModal.priceNote.grok'),
    gemini: t('rechargeModal.priceNote.gemini'),
    mj: t('rechargeModal.priceNote.mj'),
    vip: t('rechargeModal.priceNote.vip'),
    diamond_basic: t('rechargeModal.priceNote.diamondBasic'),
    diamond_vip: t('rechargeModal.priceNote.diamondVip')
  };

  const serviceItems = [
    { id: 'gpt', name: 'ChatGPT', icon: `${process.env.PUBLIC_URL}/images/gpt-main.jpg` },
    // { id: 'deepseek', name: 'DeepSeek｜满血', icon: `${process.env.PUBLIC_URL}/images/deepseek-main.jpg` },
    // { id: 'sora', name: 'Sora｜4o绘画', icon: `${process.env.PUBLIC_URL}/images/sora-car-main.jpg` },
    // { id: 'gpt_api', name: 'API防降智|试运行', icon: `${process.env.PUBLIC_URL}/images/gpt-api-car.jpg` },
    { id: 'claude', name: 'Claude', icon: `${process.env.PUBLIC_URL}/images/claude-main.jpg` },
    { id: 'grok', name: 'Grok', icon: `${process.env.PUBLIC_URL}/images/grok-main.jpg` },
    // { id: 'gemini', name: 'Gemini 2.5|试运行', icon: `${process.env.PUBLIC_URL}/images/gemini-main.jpg` },
    // { id: 'mj', name: 'Midjourney', icon: `${process.env.PUBLIC_URL}/images/mj-main.jpg` },
  ];

  const diamondServiceItems = [
    { id: 'diamond_basic', name: t('rechargeModal.diamondBasicPackage'), icon: `${process.env.PUBLIC_URL}/images/logo.jpg` },
    { id: 'diamond_vip', name: t('rechargeModal.diamondVipPackage'), icon: `${process.env.PUBLIC_URL}/images/logo.jpg` },
  ];

  const basicAvailability = {
    gpt: ['gpt','deepseek','sora'],
    mj: ['mj'],
    claude: ['claude'],
    grok: ['grok'],
    diamond: ['diamond_basic'],
    vip: ['gpt', 'mj', 'claude', 'grok'],
  };

  const tabs = [
    { id: 'gpt', label: 'GPT Plus', icon: `${process.env.PUBLIC_URL}/images/gpt-main.jpg` },
    // { id: 'grok', label: 'Grok Super', icon: `${process.env.PUBLIC_URL}/images/grok-main.jpg` },
    // { id: 'mj', label: 'MJ Basic', icon: `${process.env.PUBLIC_URL}/images/mj-main.jpg` },
    // { id: 'claude', label: 'C Pro', icon: `${process.env.PUBLIC_URL}/images/claude-main.jpg` },
    { id: 'diamond', label: t('rechargeModal.diamondCoin'), icon: `${process.env.PUBLIC_URL}/images/logo.jpg` },
  ];

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await getProducts({
        type: currentTab,
      });
      if (response.data) {
        const filteredProducts = response.data.filter(
          product => product.type === 'gpt' || product.type === 'diamond' || product.type === 'vip'
        );

        setProducts(filteredProducts);
      } else {
        console.warn(t('rechargeModal.noProductDataWarning'));
      }
    } catch (error) {
      console.error(t('rechargeModal.fetchProductsError'), error);
      setErrorMessage(t('rechargeModal.loadFailedError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      setCurrentMember('gpt'); // 强制设置为 gpt，忽略 currentTab 值
    }
  }, [isOpen, currentTab]);

  // const tabOrder = ['gpt', 'grok', 'claude', 'mj', 'diamond'];
  const tabOrder = ['gpt', 'diamond'];

  const uniqueTypes = [...new Set(products.map((p) => p.type))]
    .filter((type) => type !== 'vip')
    .sort((a, b) => tabOrder.indexOf(a) - tabOrder.indexOf(b));

  const getDurationFromDays = (days) => {
    switch (days) {
      case 30:
        return 'monthly';
      case 90:
        return 'quarterly';
      case 365:
        return 'yearly';
      default:
        return 'monthly';
    }
  };

  const priceData = products.reduce((acc, product) => {
    if (!acc[product.type]) {
      acc[product.type] = { monthly: {}, quarterly: {}, yearly: {}, fixed: {} };
    }
    if (product.type === 'diamond') {
      if (product.product_name === '3000紫刀币') {
        acc[product.type]['fixed']['basic'] = parseFloat(product.product_price_cny || '0');
      } else if (product.product_name === '10000紫刀币') {
        acc[product.type]['fixed']['vip'] = parseFloat(product.product_price_cny || '0');
      }
    } else {
      const duration = getDurationFromDays(product.product_days);
      acc[product.type][duration][product.type === 'vip' ? 'vip' : 'basic'] = parseFloat(
        product.product_price_cny || '0'
      );
    }
    return acc;
  }, {});

  const getProductTitle = (type, plan, duration) => {
    const product = products.find((p) => {
      if (type === 'diamond') {
        return p.type === 'diamond' && p.product_name === (plan === 'basic' ? '3000紫刀币' : '10000紫刀币');
      } else {
        const productDuration = getDurationFromDays(p.product_days);
        return p.type === (plan === 'vip' ? 'vip' : type) && productDuration === duration;
      }
    });
    return product?.product_name || (plan === 'vip' ? t('rechargeModal.vipMember') : t('rechargeModal.basicMember'));
  };

  const promoTexts = products.length > 0
    ? products.reduce((acc, product) => {
        if (product.type === 'diamond') {
          acc[product.type] = t('rechargeModal.promoText.diamond', { name: product.product_name });
        } else {
          acc[product.type] = t('rechargeModal.promoText.general', { name: product.product_name });
        }
        return acc;
      }, {})
    : {
        gpt: t('rechargeModal.promoText.gpt'),
        mj: t('rechargeModal.promoText.mj'),
        claude: t('rechargeModal.promoText.claude'),
        grok: t('rechargeModal.promoText.grok'),
        diamond: t('rechargeModal.promoText.diamond'),
        vip: t('rechargeModal.promoText.vip'),
      };

  const getProductId = () => {
    const selectedProduct = products.find((product) => {
      if (currentMember === 'diamond') {
        return (
          product.type === 'diamond' &&
          product.product_name === (currentPlan === 'basic' ? '3000紫刀币' : '10000紫刀币')
        );
      } else {
        const duration = getDurationFromDays(product.product_days);
        return (
          product.type === (currentPlan === 'vip' ? 'vip' : currentMember) &&
          duration === currentDuration
        );
      }
    });
    return selectedProduct?.product_id || null;
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    setSuccessUrl(null); // 清空之前的 URL

    try {
      const domain = window.location.hostname;
      const product_id = getProductId();
      if (!product_id) {
        throw new Error(t('rechargeModal.productNotFoundError'));
      }

      let channel;
      switch (currentPayment) {
        case 'alipay':
          channel = 1;
          break;
        case 'alipayHK':
          channel = 2;
          break;
        default:
          channel = 1;
          console.warn(t('rechargeModal.unknownPaymentMethod', { method: currentPayment }));
      }

      const requestData = {
        user_key,
        uid,
        product_id,
        domain,
        channel,
      };

      setAlertMessage(t('rechargeModal.fetchingPaymentPage'));
      setIsAlertModalOpen(true);

      const response = await fetchPayURL(requestData);
      const responseBody = response.data?.response?.body;
      const out_trade_no = response.out_trade_no;

      if (!responseBody) {
        throw new Error(t('rechargeModal.paymentApiError'));
      }

      const { msg, payUrl } = responseBody;

      if (msg === 'success' && payUrl) {
        localStorage.setItem('out_trade_no', out_trade_no);
        setSuccessUrl(payUrl); // 记录支付 URL
        // window.open(payUrl, '_blank'); // 新窗口打开
        window.location.href = payUrl; // 当前页面跳转
        // setAlertMessage('跳转成功，若未打开支付页面，请点击【手动跳转】 | 支付成功后刷新当前页面即可');
      } else {
        throw new Error(t('rechargeModal.paymentUrlError'));
      }
    } catch (error) {
      console.error(t('rechargeModal.paymentRequestFailed'), error);
      setAlertMessage(error.message || t('rechargeModal.paymentRetryError'));
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentPrice =
    currentMember === 'diamond'
      ? priceData[currentMember]?.['fixed']?.[currentPlan]?.toFixed(2) || '0.00'
      : currentPlan === 'vip'
      ? priceData['vip']?.[currentDuration]?.['vip']?.toFixed(2) || '0.00'
      : priceData[currentMember]?.[currentDuration]?.['basic']?.toFixed(2) || '0.00';

  const periodText =
    currentMember === 'diamond'
      ? ''
      : currentDuration === 'monthly'
      ? t('rechargeModal.perMonth')
      : currentDuration === 'quarterly'
      ? t('rechargeModal.perQuarter')
      : t('rechargeModal.perYear');

  const isServiceAvailableInBasic = (serviceId) => {
    return basicAvailability[currentMember]?.includes(serviceId) || false;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('rechargeModal.title')}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.placeholderContainer}>
              <div className={styles.placeholderTabs}></div>
              <div className={styles.placeholderSection}></div>
              <div className={styles.placeholderPlans}></div>
              <div className={styles.placeholderPayment}></div>
              <div className={styles.placeholderButton}></div>
            </div>
          ) : (
            <>
              {errorMessage && (
                <div className={styles.errorMessage}>
                  <i className="fa-solid fa-exclamation-circle"></i>
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* <div className={styles.memberTabs} >
                {uniqueTypes.map((type) => {
                  const tab = tabs.find((t) => t.id === type);
                  return (
                    <div
                      key={type}
                      className={`${styles.memberTab} ${currentMember === type ? styles.active : ''}`}
                      onClick={() => setCurrentMember(type)}
                    >
                      <img
                        src={tab?.icon || '/images/default-icon.jpg'}
                        alt={tab?.label || type}
                        className={styles.tabIcon}
                      />
                      <span className={styles.tabLabel}>{tab?.label || type.toUpperCase()}</span>
                    </div>
                  );
                })}
              </div> */}

              {/* 在 JSX 中为 memberTabs 添加 style 属性完全隐藏它 */}
              <div className={styles.memberTabs} style={{ display: 'none' }}>
                {/* 保持原有代码不变，只是通过 CSS 隐藏它 */}
                {uniqueTypes.map((type) => {
                  const tab = tabs.find((t) => t.id === type);
                  return (
                    <div
                      key={type}
                      className={`${styles.memberTab} ${currentMember === type ? styles.active : ''}`}
                      onClick={() => setCurrentMember(type)}
                    >
                      <img
                        src={tab?.icon || '/images/default-icon.jpg'}
                        alt={tab?.label || type}
                        className={styles.tabIcon}
                      />
                      <span className={styles.tabLabel}>{tab?.label || type.toUpperCase()}</span>
                    </div>
                  );
                })}
              </div>

              {userEmail && (
                <div className={styles.accountInfo}>
                  <div className={styles.accountEmail}>
                    <i className="fa-solid fa-user-circle"></i>
                    <span>{userEmail}</span>
                  </div>
                </div>
              )}

              {currentMember !== 'diamond' && (
                <div className={styles.durationSection}>
                  <div className={styles.durationSelector}>
                    <div
                      className={`${styles.durationOption} ${currentDuration === 'monthly' ? styles.active : ''}`}
                      onClick={() => setCurrentDuration('monthly')}
                    >
                      {t('rechargeModal.monthly')}
                    </div>
                    <div
                      className={`${styles.durationOption} ${currentDuration === 'quarterly' ? styles.active : ''}`}
                      onClick={() => setCurrentDuration('quarterly')}
                    >
                      {t('rechargeModal.quarterly')}
                    </div>
                    <div
                      className={`${styles.durationOption} ${currentDuration === 'yearly' ? styles.active : ''}`}
                      onClick={() => setCurrentDuration('yearly')}
                    >
                      {t('rechargeModal.yearly')}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.paymentSection}>
                <div className={styles.sectionLabel}>
                  <i className="fa-solid fa-credit-card"></i>
                  <span>{t('rechargeModal.paymentMethod')}</span>
                </div>
                <div className={styles.paymentMethods}>
                  <div
                    className={`${styles.paymentMethod} ${currentPayment === 'alipay' ? styles.selected : ''}`}
                    onClick={() => setCurrentPayment('alipay')}
                  >
                    <div className={`${styles.paymentIcon} ${styles.iconAlipay}`}>
                      <i className="fa-brands fa-alipay"></i>
                    </div>
                    <div className={styles.paymentName}>{t('rechargeModal.alipay')}</div>
                  </div>
                  <div
                    className={`${styles.paymentMethod} ${currentPayment === 'alipayHK' ? styles.selected : ''}`}
                    onClick={() => setCurrentPayment('alipayHK')}
                  >
                    <div className={`${styles.paymentIcon} ${styles.iconAlipayHK}`}>
                      <i className="fa-brands fa-alipay"></i>
                    </div>
                    <div className={styles.paymentName}>{t('rechargeModal.alipayHK')}</div>
                  </div>
                </div>
              </div>

              <div className={styles.plansContainer}>
                <div
                  className={`${styles.planCard} ${currentPlan === 'basic' ? styles.selected : ''}`}
                  onClick={() => setCurrentPlan('basic')}
                >
                  <div className={styles.planHeader}>
                    <div className={styles.planTitle}>
                      {getProductTitle(currentMember, 'basic', currentDuration)}
                    </div>
                    <div className={styles.planIcons}>
                      {currentMember === 'diamond'
                        ? diamondServiceItems
                            .filter((service) => service.id === 'diamond_basic')
                            .map((service) => (
                              <div
                                key={service.id}
                                className={`${styles.planIcon} ${styles[service.iconClass]}`}
                              >
                                <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                              </div>
                            ))
                        : serviceItems
                            .filter((service) => isServiceAvailableInBasic(service.id))
                            .map((service) => (
                              <div
                                key={service.id}
                                className={`${styles.planIcon} ${styles[service.iconClass]}`}
                              >
                                <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                              </div>
                            ))}
                    </div>
                    <div className={styles.planPrice}>
                      <span className={styles.priceCurrency}>¥</span>
                      {priceData[currentMember]?.[currentMember === 'diamond' ? 'fixed' : currentDuration]?.[
                        'basic'
                      ]?.toFixed(2) || 'N/A'}
                      <span className={styles.pricePeriod}>{periodText}</span>
                    </div>

                    <div className={styles.priceNote}>
                      {currentMember === 'diamond' 
                        ? priceNoteText['diamond_basic'] 
                        : priceNoteText[currentMember] || t('rechargeModal.basicServicePackage')}
                    </div>
                  </div>
                  <div className={styles.planFeatures}>
                    {(currentMember === 'diamond' ? diamondServiceItems : serviceItems).map(
                      (service) => (
                        <div key={service.id} className={styles.featureItem}>
                          <div className={`${styles.featureIcon} ${styles[service.iconClass]}`}>
                            <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                          </div>
                          <div className={styles.featureName}>{service.name}</div>
                          <div
                            className={`${styles.featureStatus} ${
                              isServiceAvailableInBasic(service.id)
                                ? styles.statusCheck
                                : styles.statusCross
                            }`}
                          >
                            <i
                              className={`fa-solid ${
                                isServiceAvailableInBasic(service.id) ? 'fa-check' : 'fa-xmark'
                              }`}
                            ></i>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div
                  className={`${styles.planCard} ${currentPlan === 'vip' ? styles.selected : ''}`}
                  onClick={() => setCurrentPlan('vip')}
                >
                  <div className={styles.recommendedBadge}>{t('rechargeModal.recommended')}</div>
                  <div className={styles.planHeader}>
                    <div className={styles.planTitle}>
                      {getProductTitle(currentMember === 'diamond' ? 'diamond' : 'vip', 'vip', currentDuration)}
                    </div>
                    <div className={styles.planIcons}>
                      {currentMember === 'diamond'
                        ? diamondServiceItems
                            .filter((service) => service.id === 'diamond_vip')
                            .map((service) => (
                              <div
                                key={service.id}
                                className={`${styles.planIcon} ${styles[service.iconClass]}`}
                              >
                                <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                              </div>
                            ))
                        : serviceItems.map((service) => (
                            <div
                              key={service.id}
                              className={`${styles.planIcon} ${styles[service.iconClass]}`}
                            >
                              <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                            </div>
                          ))}
                    </div>
                    <div className={styles.planPrice}>
                      <span className={styles.priceCurrency}>¥</span>
                      {currentMember === 'diamond'
                        ? priceData['diamond']?.['fixed']?.['vip']?.toFixed(2) || 'N/A'
                        : priceData['vip']?.[currentDuration]?.['vip']?.toFixed(2) || 'N/A'}
                      <span className={styles.pricePeriod}>{periodText}</span>
                    </div>

                    {/* 在这里添加价格说明文字 */}
                    <div className={styles.priceNote}>
                      {currentMember === 'diamond' 
                        ? priceNoteText['diamond_vip'] 
                        : priceNoteText['vip']}
                    </div>
                  </div>
                  <div className={styles.planFeatures}>
                    {(currentMember === 'diamond' ? diamondServiceItems : serviceItems).map(
                      (service) => (
                        <div key={service.id} className={styles.featureItem}>
                          <div className={`${styles.featureIcon} ${styles[service.iconClass]}`}>
                            <img src={service.icon} alt={service.name} className={styles.serviceIcon} />
                          </div>
                          <div className={styles.featureName}>{service.name}</div>
                          <div
                            className={`${styles.featureStatus} ${
                              currentMember === 'diamond' && service.id === 'diamond_vip'
                                ? styles.statusCheck
                                : currentMember !== 'diamond'
                                ? styles.statusCheck
                                : isServiceAvailableInBasic(service.id)
                                ? styles.statusCheck
                                : styles.statusCross
                            }`}
                          >
                            <i
                              className={`fa-solid ${
                                currentMember === 'diamond' && service.id === 'diamond_vip'
                                  ? 'fa-check'
                                  : currentMember !== 'diamond'
                                  ? 'fa-check'
                                  : isServiceAvailableInBasic(service.id)
                                  ? 'fa-check'
                                  : 'fa-xmark'
                              }`}
                            ></i>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.buttonNote}>
                <i className="fa-solid fa-info-circle"></i> 
                {t('rechargeModal.trialNote')}             
              </div>

              {/* 添加按钮下方的说明文字 */}
              <div className={styles.buttonNote}>
                <i className="fa-solid fa-info-circle"></i> 
                {t('rechargeModal.hkdNote')}               
              </div>

              <button
                className={styles.actionButton}
                onClick={handlePayment}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i> {t('rechargeModal.processing')}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check-circle"></i> {t('rechargeModal.confirmPayment')} ¥{currentPrice}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <AlertModal
        isOpen={isAlertModalOpen}
        message={alertMessage}
        onClose={() => setIsAlertModalOpen(false)}
        onConfirm={successUrl ? () => window.location.href = successUrl : null}
        confirmText={successUrl ? t('rechargeModal.manualRedirect') : t('rechargeModal.confirm')}
      />
    </div>
  );
};

export default RechargeModal;